import React, { useEffect, useState } from 'react';
import { Button, Card, Empty, Form, Input, Modal, Space, Table,Select,Checkbox,CheckboxProps } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import { useSelector } from 'react-redux';

import usecustomStyles from '../Common/customStyles';
import Spinners from '../Common/Spinner';
import { DeleteModal } from '../Common/DeleteModal';
import '../assets/scss/_custom.scss';
import { themecolor } from '../config';
import * as Thunk from '../slices/project/thunk';
import * as CThunk from '../slices/company/thunk';
import * as HPermission from '../helpers/permission_helper';

const customStyles = usecustomStyles();

const TEXT_CONSTANTS = {
    TITLE: '项目列表',
    CREATE: '新建'
};

const BaseTableColumns = [
    {
        title: '项目名称',
        dataIndex: 'projectName',
        sorter: (a, b) => a.projectName.localeCompare(b.projectName),
        sortDirections: ['ascend', 'descend'],
        align:'center'
    },
    {
        title: '公司名称',
        dataIndex: 'companyName',
        sorter: (a, b) => a.companyName.localeCompare(b.companyName),
        sortDirections: ['ascend', 'descend'],
        align:'center'
    },
    {
        title: '项目负责人',
        dataIndex: 'ownerName',
        sorter: (a, b) => a.ownerName.localeCompare(b.ownerName),
        sortDirections: ['ascend', 'descend'],
        align:'center'
    },
    {
        title: '联系方式',
        dataIndex: 'ownerPhone',
        sorter: (a, b) => a.ownerPhone.localeCompare(b.ownerPhone),
        sortDirections: ['ascend', 'descend'],
        align:'center'
    },
    {
        title: '是否接收每日异常启动短信提醒',
        dataIndex: 'isReceiveSms',
        render: (item) => 
            (
                <span>
                    <Checkbox checked={item} />
                </span>
            ),
        align:'center'
    }
];

function DataList() {
    const selector = createSelector(
        state => state.Projects,
        state => state.Company,
        state => ({
            dataList: state.dataList,
            loading: state.loading
        })
    );

    const { dataList, loading } = useSelector(selector);
    const companyList = useSelector(state => state.Company.companyList);
    const user = useSelector(state => state.Login.user);
    const companyId = user?.user?.companyId ? user?.user?.companyId : null;

    /**
     * 当前数据列表"加载中"状态
     */
    const [isLoading, setLoading] = useState(loading);

    /**
     * 当前页面的数据列表
     */
    const [localDataList, setLocalDataList] = useState([]);

    /**
     * 当前页面的数据列表的数据列
     */
    const [tableColumns, setTableColumns] = useState([]);

    /**
     * 显示删除"模态框"
     */
    const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);

    /**
     * 显示编辑"模态框"
     */
    const [editModalIsOpen, setEditModalIsOpen] = useState(false);

    /**
     * 当前操作选中的数据行
     */
    const [selectedDataRow, setSelectedDataRow] = useState();

    /**
     * 编辑弹出框是"新建"模式还是"编辑"模式
     */
    const [isCreateModel, setIsCreateModel] = useState(false);

    /**
     * 显示编辑"新建"按钮
     */
    const [showCreateBtn, setShowCreateBtn] = useState(false);

    /**
     * 显示编辑"编辑"按钮
     */
    const [showEditBtn, setShowEditBtn] = useState(false);

    /**
     * 显示编辑"删除"按钮
     */
    const [showDeleteBtn, setShowDeleteBtn] = useState(false);

    const dispatch = useDispatch();
    const [form] = Form.useForm();

    // <--------------------- useEffect --------------------->
    useEffect(() => {
        // 只有当 dataList 为空时才查询
        if (!Array.isArray(dataList) || dataList.length === 0) {
            dispatch(Thunk.getAll());
        }
        if (!Array.isArray(companyList) || companyList.length === 0) {
            dispatch(CThunk.getCompany()); 
        }
    }, []);

    useEffect(() => {
        if (Array.isArray(dataList)) {
            setLocalDataList(dataList);
        }
    }, [dispatch, dataList]);

    useEffect(() => {
        form.setFieldsValue(selectedDataRow);
    }, [selectedDataRow, form]);

    useEffect(() => {
        const role = user?.user?.role;
        setShowCreateBtn(HPermission.hasPermission(role, HPermission.PROJECTS_CREATE));
        setShowEditBtn(HPermission.hasPermission(role, HPermission.PROJECTS_UPDATE));
        setShowDeleteBtn(HPermission.hasPermission(role, HPermission.PROJECTS_DELETE));
    }, [user]);

    useEffect(() => {
        const onEditBtnClick = (item) => {
            form.resetFields();
            form.setFieldsValue(item);
            setEditModalIsOpen(true);
            setIsCreateModel(false);
        };
        const columns = [...BaseTableColumns];
        if (showEditBtn || showDeleteBtn) {
            columns.push({
                title: ' ',
                render: item => (
                    <React.Fragment>
                        <Space wrap style={{ backgroundColor: themecolor.token.colorTextDisabled, padding: '11px' }} onClick={() => setSelectedDataRow(item)}>
                            {showEditBtn && (
                                <Button type="primary" ghost onClick={()=>onEditBtnClick(item)}>
                                    修改
                                </Button>
                            )}

                            {showDeleteBtn && (
                                <Button type="primary" danger ghost onClick={onDeleteBtnClick}>
                                    删除
                                </Button>
                            )}
                        </Space>
                    </React.Fragment>
                )
            });
        }
        setTableColumns(columns);
    }, [showEditBtn, showDeleteBtn, form,selectedDataRow]);

    // <--------------------- event handler --------------------->

    function onDeleteBtnClick() {
        setDeleteModalIsOpen(true);
    }

    const onCreateBtnClick = () => {
        setEditModalIsOpen(true);
        setIsCreateModel(true);
    };

    const onEditModalClose = () => {
        setEditModalIsOpen(false);
        form.resetFields();
        setSelectedDataRow(null);
    };

    const onDeleteCanceled = () => {
        setDeleteModalIsOpen(false);
        setSelectedDataRow(null);
    };

    const onDeleteConfirmed = () => {
        if (selectedDataRow) {
            dispatch(Thunk.remove(selectedDataRow.id));
        }

        setDeleteModalIsOpen(false);
        setSelectedDataRow(null);
    };

    async function onSubmitBtnClick(values) {
        if (isCreateModel) {
            dispatch(Thunk.create(values));
        } else {
            values.id = selectedDataRow.id;
            values.companyId = selectedDataRow.companyId;
            dispatch(Thunk.update(values));
        }
        onEditModalClose();
    }

    const handleCheckboxChange = (event) => {
        setSelectedDataRow({...selectedDataRow,'isReceiveSms':event.target.checked})
        form.setFieldValue('isReceiveSms', event.target.checked);
    }

    // <--------------------- other configs --------------------->
    const pagination = {
        pageSizeOptions: ['5', '10', '20', '50'], // 页条数下拉选择器的选项
        defaultPageSize: 5, // 默认的页大小
        showSizeChanger: true, // 显示页大小改变器
    };

    const SelectStyle = { width: '100%', padding: '0px', height: 35 };

    return (
        <React.Fragment>
            {/* Delete */}
            <DeleteModal show={deleteModalIsOpen} handleClose={onDeleteCanceled} deleteModalFunction={onDeleteConfirmed} />

            {/* Api */}
            <Card style={{ marginTop: customStyles.margin, marginBottom: customStyles.margin }} className="ApiTable">
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: customStyles.margin }}>
                    <h4 style={{ fontSize: '16px', fontWeight: '500' }}>{TEXT_CONSTANTS.TITLE}</h4>
                    <div style={{ display: 'flex', flex: 'wrap' }}>
                        {showCreateBtn ? (
                            <Button type="primary" style={{ backgroundColor: customStyles.colorSuccess, boxShadow: 'none' }} onClick={onCreateBtnClick}>
                                <PlusOutlined />
                                {TEXT_CONSTANTS.CREATE}
                            </Button>
                        ) : (
                            ''
                        )}
                    </div>
                </div>
                {isLoading ? (
                    <Spinners setLoading={setLoading} />
                ) : (
                    <React.Fragment>
                        {localDataList && localDataList.length > 0 ? (
                            <div style={{ overflowX: 'auto', whiteSpace: 'nowrap' }}>
                                <Table className='table-list' rowKey="id" columns={tableColumns} dataSource={localDataList} pagination={pagination} scroll={{y:'calc(100vh - 408px)'}}/>
                            </div>
                        ) : (
                            <Empty image="/nodata.png" description={<span></span>} style={{ padding: '10%' }}></Empty>
                        )}
                    </React.Fragment>
                )}
            </Card>

            <Modal centered title={isCreateModel ? '创建项目' : '编辑项目'} open={editModalIsOpen} onCancel={onEditModalClose} footer={[]}>
                <div>
                    <Form form={form} onFinish={onSubmitBtnClick}>
                        <div>
                            <div className="project">
                                <label style={{ marginBottom: '10px', display: 'block', fontWeight: 500 }}> 项目名称 </label>
                                <Form.Item name="projectName" rules={[{ required: true, message: '项目名称不能为空' }]}>
                                    <Input name="projectName" type="text" />
                                </Form.Item>
                            </div>

                            <div className="company" style={{ marginTop: customStyles.marginSM }}>
                                <label style={{ marginBottom: '10px', display: 'block', fontWeight: 500 }}> 公司名称 </label>
                                <Form.Item name="companyId" initialValue={companyId} rules={[{ required: true, message: '公司名称不能为空' }]}>
                                    <Select name="companyId" defaultValue={companyId} disabled={companyId !== null} options={(companyList || []).map(x => ({ label: x.companyName, value: x.id }))} style={SelectStyle} />
                                </Form.Item>
                            </div>

                            <div className="owner" style={{ marginTop: customStyles.marginSM }}>
                                <label style={{ marginBottom: '10px', display: 'block', fontWeight: 500 }}> 项目负责人 </label>
                                <Form.Item name="ownerName" rules={[{ required: true, message: '负责人姓名不能为空' }]}>
                                    <Input name="ownerName" type="text" />
                                </Form.Item>
                            </div>

                            <div className="phone" style={{ marginTop: customStyles.marginSM }}>
                                <label style={{ marginBottom: '10px', display: 'block', fontWeight: 500 }}> 联系方式 </label>
                                <Form.Item name="ownerPhone" rules={[{ required: true, message: '联系方式不能为空' }]}>
                                    <Input name="ownerPhone" type="tel" pattern="+([0-9]{3}) [0-9]{2} [0-9]{3} [0-9]{4}" />
                                </Form.Item>
                            </div>

                            <div className="isReceiveSms" style={{ marginTop: customStyles.marginSM }}>
                                <label style={{ marginBottom: '10px', display: 'block', fontWeight: 500 }}> 是否接收短信 </label>
                                <Form.Item name="isReceiveSms" initialValue={form.getFieldValue('isReceiveSms')} rules={[{ required: true, message: '是否每日接收异常启动短信提示' }]}>
                                    <Checkbox name="isReceiveSms" checked={form.getFieldValue('isReceiveSms')} onChange={handleCheckboxChange}>请选择</Checkbox>
                                </Form.Item>
                            </div>
                        </div>
                        <div style={{ textAlign: 'end' }}>
                            <Button htmlType="submit" type="primary">
                                确定
                            </Button>
                        </div>
                    </Form>
                </div>
            </Modal>
        </React.Fragment>
    );
}

export default DataList;
